import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  api,
  BUNDLE_TAGS,
  BUNDLE_SECTIONS_TAG,
  BundleItemDataExtended,
  useAddToLightBundleMutation,
  useRemoveFromLightBundleMutation,
  BUNDLE_FILTER_OPTIONS_TAG,
  BUNDLE_UNDO_REDO_HISTORY_TAG,
  BUNDLE_DUPLICATES_TAG,
  BUNDLE_SIMILARS_TAG,
  refreshBundleSectionsWithCurrentFilters,
  isEndpointLockError,
} from '@/api';
import {
  BROADCAST_EXTRACT_PAGES_FROM_FULL_BUNDLE,
  BROADCAST_REMOVE_PAGES_FROM_LIGHT_BUNDLE,
  BROADCAST_FORCE_TIMELINE_REFRESH,
  BROADCAST_FORCE_CASE_OVERVIEW_CARDS_REFRESH,
  useFletchersBroadcast,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
} from '@/broadcast';
import {
  BundleType,
  makeSelectBundlePageDataByPageId,
  makeSelectBundlePageIdByPageIndex,
  selectBundlePagesData,
  selectBundlePagesDataByStoreKey,
  selectBundleSelectedThumbnailIndexes,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  selectViewportPageIndexInStore,
  setBundlePagesData,
  setBundleSelectedThumbnailIndexes,
  selectBundleMetadataByStoreKey,
  useAppDispatch,
  useAppSelector,
  selectBundlePageIds,
  selectBundleSections,
  setBundlePageIds,
  setBundleSections,
  mergePageInfo,
  PageInfoData,
  popBusyTask,
  pushBusyTask,
  selectPageInfoCache,
  selectEnabledStates,
  store,
} from '@/store';
import { FILTER_EXTRACTED_KEY } from '@/constants/filters';
import { removePageIdsFromTree, Toast } from '@repo/atomic-components';

export const useBundleInteraction = () => {
  const currentBundleMetadata = useAppSelector(selectBundleMetadataByStoreKey());
  const otherBundleId = currentBundleMetadata?.otherBundleId;
  const bundleId = useAppSelector(selectSharedDataBundleId);
  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const bundlePagesData = useAppSelector(selectBundlePagesData);
  const bundleSelectedThumbnailIndexes = useAppSelector(selectBundleSelectedThumbnailIndexes);
  const viewportPageIndexInStore = useAppSelector(selectViewportPageIndexInStore);
  const bundlePageIds = useAppSelector(selectBundlePageIds);
  const bundleSections = useAppSelector(selectBundleSections);
  const pageInfoCache = useAppSelector(selectPageInfoCache);
  const enabledStates = useAppSelector(selectEnabledStates);

  const lightBundlePagesData = useAppSelector(selectBundlePagesDataByStoreKey(BundleType.LIGHT));

  const getBundlePageIdByPageIndex = useAppSelector(makeSelectBundlePageIdByPageIndex);
  const getBundlePageDataByPageId = useAppSelector(makeSelectBundlePageDataByPageId);
  const dispatch = useAppDispatch();

  const [
    triggerAddToLightBundle,
    {
      isLoading: isAddToLightBundleLoading,
      isSuccess: isAddToLightBundleSuccess,
      isError: isAddToLightBundleError,
      error: addToLightBundleError,
      reset: addToLightBundleReset,
      originalArgs: originalAddToLightBundleArgs,
      data: addToLightBundleData,
    },
  ] = useAddToLightBundleMutation();
  const [
    triggerRemoveFromLightBundle,
    {
      isLoading: isRemoveFromLightBundleLoading,
      isSuccess: isRemoveFromLightBundleSuccess,
      isError: isRemoveFromLightBundleError,
      error: removeFromLightBundleError,
      reset: removeFromLightBundleReset,
      originalArgs: originalRemoveFromLightBundleArgs,
      data: removeFromLightBundleData,
    },
  ] = useRemoveFromLightBundleMutation();

  const toastRef = useRef<Toast>(null);

  const { sendMessageWithoutResponse, sendMessageWithResponse } = useFletchersBroadcast();

  const bundleSelectedThumbnailIds: string[] = useMemo(() => {
    return bundleSelectedThumbnailIndexes.map((pageIndex) => getBundlePageIdByPageIndex(pageIndex));
  }, [bundleSelectedThumbnailIndexes, getBundlePageIdByPageIndex]);

  const isMultipleThumbnailsSelected: boolean = useMemo(
    () => bundleSelectedThumbnailIds.length > 1,
    [bundleSelectedThumbnailIds.length],
  );

  // Surface add/remove-from-light-bundle progress in the Autosaved slot.
  useEffect(() => {
    if (!isAddToLightBundleLoading) return;
    const count = originalAddToLightBundleArgs?.pageIds?.length ?? 0;
    const id = 'add-to-light-bundle';
    dispatch(
      pushBusyTask({
        id,
        description: `Extracting ${count} ${count === 1 ? 'page' : 'pages'}...`,
      }),
    );
    return () => {
      dispatch(popBusyTask(id));
    };
  }, [isAddToLightBundleLoading, originalAddToLightBundleArgs?.pageIds?.length, dispatch]);

  useEffect(() => {
    if (!isRemoveFromLightBundleLoading) return;
    const count = originalRemoveFromLightBundleArgs?.pageIds?.length ?? 0;
    const id = 'remove-from-light-bundle';
    dispatch(
      pushBusyTask({
        id,
        description: `Removing ${count} ${count === 1 ? 'page' : 'pages'}...`,
      }),
    );
    return () => {
      dispatch(popBusyTask(id));
    };
  }, [isRemoveFromLightBundleLoading, originalRemoveFromLightBundleArgs?.pageIds?.length, dispatch]);

  useEffect(() => {
    if (isAddToLightBundleError && originalAddToLightBundleArgs) {
      addToLightBundleReset();
      // The endpoint lock has already shown a toast for this
      if (isEndpointLockError(addToLightBundleError)) return;
      toastRef.current?.show({
        severity: 'error',
        detail: `An error occurred while adding ${originalAddToLightBundleArgs.pageIds.length > 1 ? 'pages' : 'page'} to the Light Bundle`,
        life: 5000,
      });
      return;
    }

    if (isAddToLightBundleSuccess && (addToLightBundleData?.pages || originalAddToLightBundleArgs)) {
      toastRef.current?.show({
        severity: 'success',
        detail: isMultipleThumbnailsSelected
          ? 'All selected pages have been successfully added to the Light Bundle'
          : 'The selected page has been successfully added to the Light Bundle',
        life: 5000,
      });

      // Update cache from response (server-authoritative) or fallback to request args
      const cacheUpdates: Record<string, PageInfoData> = {};
      if (addToLightBundleData?.pages && Object.keys(addToLightBundleData.pages).length > 0) {
        Object.entries(addToLightBundleData.pages).forEach(([pageId, page]) => {
          cacheUpdates[pageId] = { inLightBundle: page.inLightBundle };
        });
      } else if (originalAddToLightBundleArgs?.pageIds?.length) {
        originalAddToLightBundleArgs.pageIds.forEach((id) => {
          cacheUpdates[id] = { inLightBundle: true };
        });
      }
      if (Object.keys(cacheUpdates).length > 0) {
        dispatch(mergePageInfo({ key: BundleType.FULL, data: cacheUpdates }));
      }

      // Send empty or minimal broadcast if needed, or rely on cache polling/other mechanisms?
      // The broadcast listener on line 256 uses newBundlePagesData.
      // I will disable sending the heavy payload and the local setBundlePagesData.
      // sendMessageWithoutResponse(BROADCAST_EXTRACT_PAGES_FROM_FULL_BUNDLE, { newBundlePagesData });
      dispatch(setBundleSelectedThumbnailIndexes({ key: BundleType.FULL, data: [] }));

      // Optimistically hide extracted pages from the sections tree when the filter is active
      if (enabledStates[FILTER_EXTRACTED_KEY]) {
        const extractedIds = originalAddToLightBundleArgs?.pageIds ?? [];
        if (extractedIds.length) {
          const updatedSections = removePageIdsFromTree({ tree: bundleSections, pageIds: extractedIds });
          const idsSet = new Set(extractedIds);
          const updatedPageIds = bundlePageIds.filter((id) => !idsSet.has(id));
          dispatch(setBundleSections({ key: BundleType.FULL, data: updatedSections }));
          dispatch(setBundlePageIds({ key: BundleType.FULL, data: updatedPageIds }));
        }
      }

      sendMessageWithoutResponse(BROADCAST_FORCE_TIMELINE_REFRESH, {
        reason: 'Extract success',
      });
      sendMessageWithoutResponse(BROADCAST_FORCE_CASE_OVERVIEW_CARDS_REFRESH, {
        reason: 'Extract success',
      });

      // The extracted page now lives in the Light Bundle too, so duplicate and
      // similar relationships have changed. Refetch them in this (Full Bundle)
      // tab so its tooltips update without a full page reload. The Light Bundle
      // tab is refreshed separately via its extraction broadcast handler.
      if (originalAddToLightBundleArgs?.bundleId) {
        dispatch(
          api.util.invalidateTags([
            { type: BUNDLE_DUPLICATES_TAG, id: originalAddToLightBundleArgs.bundleId },
            { type: BUNDLE_SIMILARS_TAG, id: originalAddToLightBundleArgs.bundleId },
            { type: BUNDLE_UNDO_REDO_HISTORY_TAG, id: originalAddToLightBundleArgs.bundleId },
          ]),
        );
      }

      addToLightBundleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addToLightBundleData,
    bundlePagesData,
    isAddToLightBundleError,
    isAddToLightBundleSuccess,
    isMultipleThumbnailsSelected,
    originalAddToLightBundleArgs,
  ]);

  useEffect(() => {
    if (isRemoveFromLightBundleError && originalRemoveFromLightBundleArgs) {
      removeFromLightBundleReset();
      // The endpoint lock has already shown a toast for this
      if (isEndpointLockError(removeFromLightBundleError)) return;
      toastRef.current?.show({
        severity: 'error',
        detail: `An error occurred while removing ${originalRemoveFromLightBundleArgs.pageIds.length > 1 ? 'pages' : 'page'} from the Light Bundle`,
        life: 5000,
      });
      return;
    }

    if (isRemoveFromLightBundleSuccess && originalRemoveFromLightBundleArgs) {
      toastRef.current?.show({
        severity: 'success',
        detail: isMultipleThumbnailsSelected
          ? 'All selected pages have been successfully removed from the Light Bundle'
          : 'The selected page has been successfully removed from the Light Bundle',
        life: 5000,
      });

      sendMessageWithoutResponse(BROADCAST_REMOVE_PAGES_FROM_LIGHT_BUNDLE, {
        pageIdsToUpdate: originalRemoveFromLightBundleArgs.pageIds,
      });

      dispatch(setBundleSelectedThumbnailIndexes({ key: BundleType.LIGHT, data: [] }));
      dispatch(
        api.util.invalidateTags([
          { type: BUNDLE_FILTER_OPTIONS_TAG, id: originalRemoveFromLightBundleArgs.bundleId },
          { type: BUNDLE_UNDO_REDO_HISTORY_TAG, id: originalRemoveFromLightBundleArgs.bundleId },
        ]),
      );

      // Update cache in current tab
      const cacheUpdates: Record<string, PageInfoData> = {};
      originalRemoveFromLightBundleArgs.pageIds.forEach((id) => {
        cacheUpdates[id] = { inLightBundle: false };
      });
      // We update cache for FULL bundle since that's where the visual checkmark matters
      dispatch(mergePageInfo({ key: BundleType.FULL, data: cacheUpdates }));

      if (
        activeBundleType === BundleType.LIGHT &&
        removeFromLightBundleData &&
        Array.isArray(removeFromLightBundleData.pageIds) &&
        removeFromLightBundleData.pageIds.length
      ) {
        const newLightPagesData = new Map<string, BundleItemDataExtended>(lightBundlePagesData.entries());

        removeFromLightBundleData.pageIds.forEach((id: string) => {
          if (newLightPagesData.has(id)) {
            newLightPagesData.delete(id);
          }
        });

        dispatch(
          setBundlePagesData({
            key: BundleType.LIGHT,
            data: newLightPagesData,
          }),
        );

        const newLightBundlePageIds = bundlePageIds.filter((id) => !removeFromLightBundleData.pageIds.includes(id));

        const newBundleSections = removePageIdsFromTree({
          tree: bundleSections,
          pageIds: removeFromLightBundleData.pageIds,
        });

        if (!newLightBundlePageIds.length || !newBundleSections.length) {
          refreshBundleSectionsWithCurrentFilters(dispatch, store.getState());
        } else {
          dispatch(
            setBundlePageIds({
              key: BundleType.LIGHT,
              data: newLightBundlePageIds,
            }),
          );

          dispatch(
            setBundleSections({
              key: BundleType.LIGHT,
              data: newBundleSections,
            }),
          );
        }
      }

      sendMessageWithoutResponse(BROADCAST_FORCE_TIMELINE_REFRESH, {
        reason: 'Remove success',
      });
      sendMessageWithoutResponse(BROADCAST_FORCE_CASE_OVERVIEW_CARDS_REFRESH, {
        reason: 'Remove success',
      });
      removeFromLightBundleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMultipleThumbnailsSelected,
    isRemoveFromLightBundleError,
    isRemoveFromLightBundleSuccess,
    originalRemoveFromLightBundleArgs,
  ]);

  useEffect(() => {
    /**
     * Broadcast listener
     */
    const controller = new AbortController();
    const signal = controller.signal;

    const extractPagesFromFullBundle = (event: CustomEvent) => {
      if (signal.aborted) {
        console.info(`Listener Aborted: ${BROADCAST_EXTRACT_PAGES_FROM_FULL_BUNDLE}`);
        return;
      }

      if (activeBundleType === BundleType.LIGHT) {
        refreshBundleSectionsWithCurrentFilters(dispatch, store.getState());
        dispatch(api.util.invalidateTags([...BUNDLE_TAGS].filter((tag) => tag !== BUNDLE_SECTIONS_TAG)));
      }

      /**
       * if the user extracted some pages, we have to update pages
       * in Full Bundle in another Full Bundle tab (if it exists)
       */
      if (activeBundleType === BundleType.FULL && event.detail.payload.newBundlePagesData) {
        dispatch(
          setBundlePagesData({
            key: BundleType.FULL,
            data: event.detail.payload.newBundlePagesData,
          }),
        );
      }
    };

    window.addEventListener(BROADCAST_EXTRACT_PAGES_FROM_FULL_BUNDLE, extractPagesFromFullBundle as EventListener, {
      signal,
    });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBundleType]);

  useEffect(() => {
    /**
     * Broadcast listener
     */
    const controller = new AbortController();
    const signal = controller.signal;

    const removePagesFromLightBundle = (event: CustomEvent) => {
      if (signal.aborted) {
        console.info(`Listener Aborted: ${BROADCAST_REMOVE_PAGES_FROM_LIGHT_BUNDLE}`);
        return;
      }

      if (event.detail.payload.pageIdsToUpdate) {
        // Update cache from broadcast for Full Bundle (to ensure ticks are removed)
        const cacheUpdates: Record<string, PageInfoData> = {};
        event.detail.payload.pageIdsToUpdate.forEach((id: string) => {
          cacheUpdates[id] = { inLightBundle: false };
        });
        dispatch(mergePageInfo({ key: BundleType.FULL, data: cacheUpdates }));
      }

      /**
       * if the user removed some pages, we have to update pages
       * in Light Bundle in another Light Bundle tab (if it exists)
       */
      if (activeBundleType === BundleType.LIGHT) {
        refreshBundleSectionsWithCurrentFilters(dispatch, store.getState());
        dispatch(api.util.invalidateTags([...BUNDLE_TAGS].filter((tag) => tag !== BUNDLE_SECTIONS_TAG)));
      }
    };

    window.addEventListener(BROADCAST_REMOVE_PAGES_FROM_LIGHT_BUNDLE, removePagesFromLightBundle as EventListener, {
      signal,
    });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBundleType, bundlePagesData]);

  const pageId: string | undefined = useMemo(
    () => getBundlePageIdByPageIndex(viewportPageIndexInStore),
    [getBundlePageIdByPageIndex, viewportPageIndexInStore],
  );

  const pageData: BundleItemDataExtended | undefined = useMemo(() => {
    if (pageId) {
      return getBundlePageDataByPageId(pageId);
    }
  }, [getBundlePageDataByPageId, pageId]);

  const pageIdsNotInLightBundle: string[] = useMemo(
    () =>
      activeBundleType === BundleType.FULL
        ? bundleSelectedThumbnailIds
            .map((id) => {
              const item = getBundlePageDataByPageId(id);
              const cached = pageInfoCache[id];
              // Use cached value if available, or fallback to item data
              const isInLightBundle = cached?.inLightBundle ?? item?.inLightBundle;
              return { id, isInLightBundle };
            })
            .filter((inf) => !inf.isInLightBundle)
            .map((inf) => inf.id)
        : [],
    [activeBundleType, bundleSelectedThumbnailIds, getBundlePageDataByPageId, pageInfoCache],
  );

  const canExtract: boolean = useMemo(() => {
    if (pageIdsNotInLightBundle.length) return true;
    if (!pageId) return false;
    const cached = pageInfoCache[pageId];
    return !(cached?.inLightBundle ?? pageData?.inLightBundle);
  }, [pageData?.inLightBundle, pageIdsNotInLightBundle.length, pageId, pageInfoCache]);
  const getPageToScrollTo = useCallback(() => {
    // Simple rule: if array length > 2 and pages are consecutive (diff = 1), use index 1
    if (pageIdsNotInLightBundle.length > 2) {
      // Get page numbers from the first two elements to check if consecutive
      const firstPageData = getBundlePageDataByPageId(pageIdsNotInLightBundle[0]);
      const secondPageData = getBundlePageDataByPageId(pageIdsNotInLightBundle[pageIdsNotInLightBundle.length - 1]);

      if (firstPageData && secondPageData) {
        const pageDiff = Math.abs(firstPageData.originalPageNumber - secondPageData.originalPageNumber);
        if (pageDiff === 1) {
          // Use index 1 (second element) for consecutive range selections
          return pageIdsNotInLightBundle[1];
        }
      }
    }

    // Fallback: use last page (original behavior)
    return pageIdsNotInLightBundle[pageIdsNotInLightBundle.length - 1];
  }, [pageIdsNotInLightBundle, getBundlePageDataByPageId]);
  const handleExtract = useCallback((): Promise<boolean> => {
    if (!bundleId) return Promise.resolve(false);

    if (pageId && !isMultipleThumbnailsSelected) {
      return triggerAddToLightBundle({ bundleId, pageIds: [pageId] })
        .unwrap()
        .then(() => {
          sendMessageWithResponse(
            BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
            { showPageIdInViewport: pageId, bundleId: otherBundleId },
            1000,
          )
            .then((response) => {
              if (response instanceof Window) response.focus();
            })
            .catch((error) => console.info(error.message));
          return true;
        })
        .catch((error) => {
          console.info(error.message);
          return false;
        });
    }

    if (!pageIdsNotInLightBundle.length) {
      toastRef.current?.show({
        severity: 'info',
        detail: 'All selected pages are already in Light Bundle',
        life: 5000,
      });
      return Promise.resolve(false);
    }

    return triggerAddToLightBundle({ bundleId, pageIds: pageIdsNotInLightBundle })
      .unwrap()
      .then(() => {
        sendMessageWithResponse(
          BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE_WHEN_EXTRACTED_FROM_FULL_BUNDLE,
          {
            showPageIdInViewport: getPageToScrollTo(),
            bundleId: otherBundleId,
          },
          1000,
        )
          .then((response) => {
            if (response instanceof Window) response.focus();
          })
          .catch((error) => console.info(error.message));
        return true;
      })
      .catch((error) => {
        console.info(error.message);
        return false;
      });
  }, [
    bundleId,
    otherBundleId,
    isMultipleThumbnailsSelected,
    pageId,
    getPageToScrollTo,
    pageIdsNotInLightBundle,
    sendMessageWithResponse,
    triggerAddToLightBundle,
  ]);

  const handleRemove = useCallback(() => {
    if (!bundleId) {
      return;
    }

    /** will remove the page where user stopped scrolling */
    if (pageId && !isMultipleThumbnailsSelected) {
      triggerRemoveFromLightBundle({ bundleId, pageIds: [pageId] });
      return;
    }

    /** user selected multiple thumbnails */
    triggerRemoveFromLightBundle({
      bundleId,
      pageIds: bundleSelectedThumbnailIds,
    });
  }, [bundleId, bundleSelectedThumbnailIds, isMultipleThumbnailsSelected, pageId, triggerRemoveFromLightBundle]);

  const removablePageCount = isMultipleThumbnailsSelected ? bundleSelectedThumbnailIds.length : 1;

  return {
    activeBundleType,
    canExtract,
    extractablePageCount: pageIdsNotInLightBundle.length || (canExtract ? 1 : 0),
    removablePageCount,
    isAddToLightBundleLoading,
    isRemoveFromLightBundleLoading,
    toastRef,
    handleExtract,
    handleRemove,
  };
};
